// Unified thumbnail fallback system
// Handles all thumbnail logic with proper fallbacks

// Extract YouTube video ID from URL
export const extractYouTubeVideoId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
    /youtube\.com\/embed\/([^&\n?#]+)/,
    /youtube\.com\/v\/([^&\n?#]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url?.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Extract TikTok video ID from URL
export const extractTikTokVideoId = (url) => {
  if (!url) return null;
  
  // TikTok URL patterns:
  // https://www.tiktok.com/@username/video/1234567890
  // https://vm.tiktok.com/ABC123/
  // https://tiktok.com/@username/video/1234567890
  const patterns = [
    /tiktok\.com\/@[\w\.]+\/video\/(\d+)/,
    /vm\.tiktok\.com\/([\w]+)/,
    /tiktok\.com\/t\/([\w]+)/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Get platform icon for fallback
export const getPlatformIcon = (channel) => {
  const icons = {
    'Instagram': '📷',
    'YouTube': '📺',
    'TikTok': '🎵',
    'Twitter': '🐦',
    'Facebook': '👥',
    'LinkedIn': '💼',
    'Pinterest': '📌',
    'Web': '🌐',
    'Unknown': '📄'
  };
  return icons[channel] || icons['Unknown'];
};

// Get platform-specific placeholder
export const getPlatformPlaceholder = (channel) => {
  const placeholders = {
    'Instagram': '/assets/instagram-placeholder.svg',
    'YouTube': '/assets/youtube-placeholder.svg',
    'TikTok': '/assets/tiktok-placeholder.svg',
    'Twitter': '/assets/default-thumbnail.svg',
    'Facebook': '/assets/default-thumbnail.svg',
    'LinkedIn': '/assets/default-thumbnail.svg',
    'Pinterest': '/assets/default-thumbnail.svg',
    'Web': '/assets/default-thumbnail.svg'
  };
  return placeholders[channel] || '/assets/default-thumbnail.svg';
};

// Check if URL is a video file (for conversion to static image)
export const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.gif'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext)) || 
         lowerUrl.includes('video/') || 
         lowerUrl.includes('video%2F');
};

// Convert video thumbnail URL to static image by extracting first frame
// Returns a promise that resolves to a data URL (base64 image)
export const convertVideoThumbnailToStatic = (videoUrl) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    
    video.addEventListener('loadedmetadata', () => {
      // Seek to first frame
      video.currentTime = 0.1; // Small offset to ensure frame is loaded
    });
    
    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert to JPEG data URL
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        resolve(dataUrl);
      } catch (error) {
        console.error('Error converting video thumbnail:', error);
        reject(error);
      }
    });
    
    video.addEventListener('error', (e) => {
      console.error('Error loading video for thumbnail:', e);
      reject(new Error('Failed to load video'));
    });
    
    // Set timeout to prevent hanging
    setTimeout(() => {
      reject(new Error('Video thumbnail conversion timeout'));
    }, 5000);
    
    video.src = videoUrl;
  });
};

// Helper to check if URL is a valid thumbnail URL (not a webpage)
const isValidThumbnailUrl = (url) => {
  if (!url) return false;
  
  // Reject Instagram page URLs (these are NOT images)
  if (url.includes('instagram.com') && !url.includes('cdninstagram') && !url.includes('fbcdn')) {
    return false;
  }
  
  // Reject TikTok page URLs
  if (url.includes('tiktok.com') && !url.includes('muscdn')) {
    return false;
  }
  
  // Reject YouTube page URLs (but allow ytimg.com)
  if (
    (url.includes('youtube.com') || url.includes('youtu.be')) &&
    !url.includes('ytimg.com') &&
    !url.includes('i.ytimg') &&
    !url.includes('img.youtube.com')
  ) {
    return false;
  }
  
  // Reject Twitter page URLs (but allow twimg)
  if ((url.includes('twitter.com') || url.includes('x.com')) && !url.includes('twimg')) {
    return false;
  }
  
  // Must start with http:// or https:// or be a data URL
  if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
    return false;
  }
  
  // Looks valid
  return true;
};

// Main thumbnail URL resolver
export const getThumbnailUrl = (content) => {
  // Normalize channel: deepsearch items often store it under meta.channel
  const channel = content?.channel || content?.meta?.channel || null;

  // HIGHEST PRIORITY: GetTranscribe S3 thumbnails are ALWAYS valid (from transcription service)
  if (content?.thumbnail_url && content.thumbnail_url.includes('gettranscribe.s3')) {
    return content.thumbnail_url;
  }
  if (content?.image_url && content.image_url.includes('gettranscribe.s3')) {
    return content.image_url;
  }

  // YouTube: always derive a thumbnail from the URL when possible (channel is sometimes missing/mis-labeled)
  if (typeof content?.url === 'string') {
    const u = content.url;
    if ((u.includes('youtube.com') || u.includes('youtu.be')) && !u.includes('ytimg.com') && !u.includes('i.ytimg') && !u.includes('img.youtube.com')) {
      const videoId = extractYouTubeVideoId(u);
      if (videoId) {
        return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
      }
    }
  }

  // CRITICAL FIX: For video content, prioritize GetTranscribe thumbnails first
  if (content?.source_type === 'video') {
    // 1. First check thumbnail_url if it's a GetTranscribe S3 URL (always valid and highest priority)
    if (content.thumbnail_url && content.thumbnail_url.includes('gettranscribe.s3')) {
      return content.thumbnail_url;
    }
    
    // 2. For Instagram/TikTok, try image_url (this is where actual thumbnails are stored)
    if ((channel === 'Instagram' || channel === 'TikTok' || channel === 'YouTube' || content?._source === 'deepsearch.items') && content.image_url && isValidThumbnailUrl(content.image_url) && !content.image_url.includes('/reel/')) {
      if (channel === 'TikTok' && isVideoUrl(content.image_url)) {
        return content.image_url; // Will be converted in component
      }
      return content.image_url;
    }
    
    // 3. Try meta fields for videos (check all possible field names)
    if (content?.meta?.thumbnail_url && isValidThumbnailUrl(content.meta.thumbnail_url) && !content.meta.thumbnail_url.includes('/reel/')) {
      return content.meta.thumbnail_url;
    }
    if (content?.meta?.image_url && isValidThumbnailUrl(content.meta.image_url) && !content.meta.image_url.includes('/reel/')) {
      return content.meta.image_url;
    }
    if (content?.meta?.thumbnail && isValidThumbnailUrl(content.meta.thumbnail) && !content.meta.thumbnail.includes('/reel/')) {
      return content.meta.thumbnail;
    }
    if (content?.meta?.cover_image && isValidThumbnailUrl(content.meta.cover_image) && !content.meta.cover_image.includes('/reel/')) {
      return content.meta.cover_image;
    }
    if (content?.meta?.preview_url && isValidThumbnailUrl(content.meta.preview_url) && !content.meta.preview_url.includes('/reel/')) {
      return content.meta.preview_url;
    }
    
    // 4. Only use thumbnail_url if it's not a video URL and passes validation
    if (content.thumbnail_url && isValidThumbnailUrl(content.thumbnail_url) && !content.thumbnail_url.includes('/reel/') && !content.thumbnail_url.includes('instagram.com/p/')) {
      if (channel === 'TikTok' && isVideoUrl(content.thumbnail_url)) {
        return content.thumbnail_url; // Will be converted in component
      }
      return content.thumbnail_url;
    }

    // 5. YouTube fallback (again) for video items that don't have channel set correctly
    if (typeof content?.url === 'string') {
      const u = content.url;
      if ((u.includes('youtube.com') || u.includes('youtu.be')) && !u.includes('ytimg.com') && !u.includes('i.ytimg') && !u.includes('img.youtube.com')) {
        const videoId = extractYouTubeVideoId(u);
        if (videoId) {
          return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
        }
      }
    }
  }
  
  // For non-video content, use original priority
  // 1. Try content's existing thumbnail first
  // IMPORTANT: Validate that thumbnail_url is actually an image URL
  if (content?.thumbnail_url && isValidThumbnailUrl(content.thumbnail_url)) {
    // Check if TikTok thumbnail is a video file - mark it for conversion
    if (channel === 'TikTok' && isVideoUrl(content.thumbnail_url)) {
      // Return a special marker that components can detect
      // Components will handle the conversion on-the-fly
      return content.thumbnail_url; // Will be converted in component
    }
    return content.thumbnail_url;
  }
  
  // 2. CRITICAL: Check image_url (this is where video thumbnails are stored!)
  // IMPORTANT: Validate that image_url is actually an image, not a webpage URL
  if (content?.image_url && isValidThumbnailUrl(content.image_url)) {
    // Check if TikTok thumbnail is a video file - mark it for conversion
    if (channel === 'TikTok' && isVideoUrl(content.image_url)) {
      return content.image_url; // Will be converted in component
    }
    return content.image_url;
  }
  
  // 3. Try meta fields (from deepsearch.items.meta)
  // Check multiple possible meta field names for video thumbnails
  // IMPORTANT: All must be validated to not be webpage URLs
  if (content?.meta?.thumbnail_url && isValidThumbnailUrl(content.meta.thumbnail_url)) {
    return content.meta.thumbnail_url;
  }
  if (content?.meta?.image_url && isValidThumbnailUrl(content.meta.image_url)) {
    return content.meta.image_url;
  }
  if (content?.meta?.thumbnail && isValidThumbnailUrl(content.meta.thumbnail)) {
    return content.meta.thumbnail;
  }
  if (content?.meta?.cover_image && isValidThumbnailUrl(content.meta.cover_image)) {
    return content.meta.cover_image;
  }
  if (content?.meta?.preview_url && isValidThumbnailUrl(content.meta.preview_url)) {
    return content.meta.preview_url;
  }
  
  // 4. Try transcription field (GetTranscribe stores thumbnails here for TikTok/Instagram)
  if (content?.transcription?.thumbnail_url) {
    return content.transcription.thumbnail_url;
  }
  if (typeof content?.transcription === 'object' && content.transcription?.thumbnail) {
    return content.transcription.thumbnail;
  }
  
  // 5. Try file_info thumbnail
  if (content?.file_info?.thumbnail_url) {
    if (channel === 'TikTok' && isVideoUrl(content.file_info.thumbnail_url)) {
      return content.file_info.thumbnail_url; // Will be converted in component
    }
    return content.file_info.thumbnail_url;
  }
  
  // 6. For images, use the URL directly if it's an image file
  if (content?.source_type === 'image' && content?.url && (content.url.startsWith('http') || content.url.startsWith('/'))) {
    return content.url;
  }
  
  // 7. Platform-specific extraction
  if (content?.url) {
    // YouTube (channel optional)
    if ((content.url.includes('youtube.com') || content.url.includes('youtu.be')) && !content.url.includes('ytimg.com') && !content.url.includes('i.ytimg') && !content.url.includes('img.youtube.com')) {
      const videoId = extractYouTubeVideoId(content.url);
      if (videoId) return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
    }
    
    // For Instagram, check if it's an image URL or if we have stored thumbnail
    if (channel === 'Instagram') {
      // If it's an image URL, use it directly
      if (content.url && (content.url.match(/\.(jpg|jpeg|png|gif|webp)/i) || content.content_type === 'image')) {
        return content.url;
      }
      // Otherwise placeholder
      return getPlatformPlaceholder('Instagram');
    }
    
    // For TikTok, try to extract video ID and use TikTok's thumbnail API
    // TikTok doesn't have a public thumbnail API like YouTube, but we can try
    // using the video ID with third-party services or fallback to placeholder
    if (channel === 'TikTok') {
      // Check if we have a stored thumbnail_url from transcription/processing
      if (content.thumbnail_url && !content.thumbnail_url.includes('placeholder')) {
        // If it's a video, mark it for conversion
        if (isVideoUrl(content.thumbnail_url)) {
          return content.thumbnail_url; // Will be converted in component
        }
        return content.thumbnail_url;
      }
      // TikTok videos processed through GetTranscribe should have thumbnails
      // If not available, fallback to placeholder
      return getPlatformPlaceholder('TikTok');
    }
  }
  
  // 8. Fallback to platform-specific placeholder
  return getPlatformPlaceholder(channel || 'Unknown');
};

// Check if URL is a valid image
export const isValidImageUrl = (url) => {
  if (!url) return false;
  
  // Check if it's a data URL
  if (url.startsWith('data:')) return true;
  
  // Check if it's a valid HTTP(S) URL
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
};

// Generate thumbnail for different content types
export const generateThumbnailForContent = (content) => {
  const thumbnailUrl = getThumbnailUrl(content);
  const platformIcon = getPlatformIcon(content?.channel || 'Unknown');
  
  return {
    url: thumbnailUrl,
    platformIcon,
    isPlaceholder: !content?.thumbnail_url && !content?.file_info?.thumbnail_url,
    fallbackText: content?.title?.substring(0, 2)?.toUpperCase() || platformIcon
  };
};
